import { Form, useFormikContext } from 'formik';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParam } from 'react-use';
import { useWizard } from 'react-use-wizard';

import { Button } from '../../components/button';
import FormContainer from '../../components/containers/FormContainer';
import Control from '../../components/form/Control';
import Img from '../../components/img/Img';
import { alertConstants } from '../../redux/constants';
import routes from '../../routes/constants';
import text from '../../text';
import { larkiApiNoAuthentication } from '../../utilities/api';
import { getUser } from '../../utilities/storage';
import { isUserAnonymous } from '../../utilities/user';
import Header from './Header';
import SocialLoginButton from './SocialLoginButton';

const SignupForm = ({ onGoogleRedirect, onMicrosoftRedirect, onGoToLogin }) => {
  const dispatch = useDispatch();
  const {
    values,
    errors,
    touched,
    isSubmitting,
    dirty,
    handleChange,
    handleBlur,
    submitForm,
    isValid,
  } = useFormikContext();
  const { nextStep, goToStep } = useWizard();
  const [isLoading, setLoading] = useState(false);
  const redirectUrl = useSearchParam('redirectUrl');
  const onRevisit = useSearchParam('onRevisit');
  const { currentUser } = useSelector((state) => ({
    currentUser: state.profileReducer.userProfile,
  }));

  const onSubmit = async (event) => {
    try {
      event.preventDefault();
      setLoading(true);
      let headers = {};
      if (isUserAnonymous(currentUser)) {
        headers['Authorization'] = `Bearer ${getUser().token}`;
      }
      const data = await submitForm();
      if (data) {
        const { is_google_account, is_microsoft_account, is_verified, exists } =
          data;

        if (!exists) {
          nextStep(); //user does not exist, go to create new account
        } else {
          if (is_google_account) {
            await onGoogleRedirect();
          } else if (is_microsoft_account) {
            await onMicrosoftRedirect();
          } else if (is_verified) {
            onGoToLogin(values.email);
          }

          if (!is_verified) {
            await larkiApiNoAuthentication.post(
              '/user/send-verification-email',
              {
                ...values,
                ...(redirectUrl && { redirectUrl }),
                ...(onRevisit && { onRevisit }),
              },
              { headers }
            );
            sessionStorage.clear();
            goToStep(2);
          }
        }
      }
    } catch (error) {
      dispatch({
        type: alertConstants.ALERT_ERROR,
        payload: { message: error.response.data.message },
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSocialRedirect = (redirect) => async () => {
    setLoading(true);
    await redirect();
  };

  return (
    <FormContainer className='SignupForm'>
      <FormContainer.SecondaryHeader isSecondary>
        <Header />
      </FormContainer.SecondaryHeader>
      <FormContainer.Body>
        <FormContainer.PrimaryHeader>
          <div>
            <h2>{text('welcomeToLarki')}</h2>
          </div>
          <p>{text('chooseOptionsToGetStarted', {
            emphasizedText: <span className='font-weight--700 font-italic'>work</span>
          })}</p>
        </FormContainer.PrimaryHeader>
        <Form noValidate onSubmit={onSubmit}>
          <SocialLoginButton
            onClick={handleSocialRedirect(onMicrosoftRedirect)}
            iconSrc='/public/img/icon-microsoft.svg'
            label={text('signUpLoginWithMicrosoft')}
          />
          <SocialLoginButton
            onClick={handleSocialRedirect(onGoogleRedirect)}
            iconSrc='/public/img/icon-google.svg'
            label={text('signUpLoginWithGoogle')}
          />
          <div className='or'>
            <span>{text('or')}</span>
          </div>
          <Control
            validationError={errors.email}
            as='input'
            type='text'
            name='email'
            label={text('email')}
            value={values?.email || ''}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder={text('enterEmail')}
            isFailed={touched.email && Boolean(errors.email)}
          />
          <div className='text-center'>
            <div className='btns-group'>
              <Button
                className='full-width'
                loading={isSubmitting || isLoading}
                disabled={!dirty || !isValid}
                onClick={onSubmit}
              >
                {text('signUpLogin')}
              </Button>
            </div>
            <p className='small-text'>
              {text('createAccountByline')}
              <br />
              <a
                href={process.env.LARKI_URL_TERMS}
                target='_blank'
                rel='noreferrer'
              >
                {text('termsOfUse')}
              </a>{' '}
              {text('and')}{' '}
              <a
                href={routes.larkiWebsiteUrl.privacy}
                target='_blank'
                rel='noreferrer'
              >
                {text('privacyPolicy')}
              </a>
            </p>
          </div>
        </Form>
        <Img id='laptop-larki-form' img='laptop-larki-app' />
      </FormContainer.Body>
    </FormContainer>
  );
};

export default SignupForm;
